<template>
  <v-container fill-height justify-content-start class="app-section-width pa-0">
    <v-layout v-if="activities.extendedActivity" column>
      <div class="activity-header pb-10">
        <div class="activity-title" :class="{ 'pl-0': $vuetify.breakpoint.xsOnly }">
          <v-icon @click="$router.go(-1)" color="white" class="ms-3 me-3">mdi-arrow-left</v-icon>
          <v-toolbar-title>{{ activities.extendedActivity.activity.name }}</v-toolbar-title>
        </div>
        <v-img :set="url = activities.extendedActivity.activity.coverImageUrl" :lazy-src="url" :src="url" />
      </div>
      <reservation-type-chip :reservationMethod="reservationMethod" />
      <v-flex class="app-scroll-section pa-2" v-if="!loadingWorkingHours && reservationMethod">
        <v-form ref="form" autocomplete="off" class="py-2">
          <v-container>
            <v-layout row wrap>
              <!-- huésped -->
              <v-flex xs12>
                <app-dropdown
                  :label="$t('fields.in_whose_name')"
                  filled
                  prependInnerIcon="mdi-account"
                  :items="availableGuests"
                  :rules="[rules.required]"
                  :value="guest"
                  @change="(value) => guest = value"
                  returnObject
                  itemValue="guest.id"
                  class="notranslate required"
                  hasSelectionSlot
                  hasItemSlot
                >
                  <template v-slot:selection="{ item }">
                    {{ item.profile.name }} {{ item.profile.lastName || null }}
                  </template>
                  <template v-slot:item="{ item }">
                    <v-layout>
                      <div>{{ item.profile.name }} {{ item.profile.lastName || null }}</div>
                      <v-spacer/>
                      <div v-if="!item.guest.active">{{$t('reservations.inactive_guest')}}</div>
                      <div v-else-if="item.disabled">{{$t('reservations.no_account')}}</div>
                    </v-layout>
                  </template>
                </app-dropdown>
              </v-flex>
              <template v-if="guest">
                <reservation-date-and-time-selector 
                  :reservationMethod="reservationMethod" 
                  :activityId="$route.params.activityId"
                  @checkAnotherReservation="({date, slot}) => getAnotherReservationForGroup(date, slot)"/>
                <v-alert v-if="anotherReservation" dense  type="error" color="red" text>{{
                  $t('activity_reservation.another_reservation_restriction',
                  {
                    activityName: anotherReservation.activity.name,
                    activityTime: getReservationTime(anotherReservation.reservation)
                  })
                }}</v-alert>
                <!-- nominal attendees -->
                <template v-if="slot">
                  <v-flex xs12 v-if="reservationMethod.attendeeTypeConfiguration === attendeeTypeConfigurationTypes.NOMINAL">
                    <app-dropdown
                      :label="$t('fields.attendees')"
                      filled
                      prependInnerIcon="mdi-account-group"
                      :items="selectableNominalGuests"
                      :value="nominalAttendees"
                      :rules="nominalAttendees.length ? [] : [rules.oneOrMoreNominalAttendees]"
                      @change="(value) => nominalAttendees = value"
                      itemValue="guest.id"
                      class="notranslate required"
                      hasSelectionSlot
                      hasItemSlot
                      multiple
                    >
                      <template v-slot:selection="{ item }">
                        <v-chip
                          close
                          class="mt-2"
                          small
                          @click:close="nominalAttendees = nominalAttendees.filter(nA => nA !== item.guest.id)">
                          {{ item.profile.name }} {{ item.profile.lastName || null }}
                        </v-chip>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-layout>
                          <div>{{ item.profile.name }} {{ item.profile.lastName || null }}</div>
                          <v-spacer/>
                          <div v-if="item.notNominal">{{$t('reservations.guest_type_not_selectable')}}</div>
                        </v-layout>
                      </template>
                    </app-dropdown>
                  </v-flex>
                  <!-- numeral attendees -->
                  <v-flex
                    xs12
                    d-flex flex-wrap
                    v-if="reservationMethod.attendeeTypeConfiguration === attendeeTypeConfigurationTypes.NUMERAL">
                    <v-flex
                      v-for="(attendeeType, i) in attendeeTypes"
                      :key="i"
                      xs12 md6>
                        <attendees-selector :attendeeType="attendeeType" class="px-4" ref="quantity" />
                    </v-flex>
                  </v-flex>
                </template>
                <v-alert dense v-if="getCapacityStatus({ over: true })" type="error" color="red" text>
                  {{ $t("activity_reservation.over_capacity") }}
                </v-alert>
                <!--DELIVER ROOM-->
                <v-flex xs12 v-if="reservationMethodType === 'ROOM_SERVICE'">
                  <v-autocomplete
                    v-if="rooms.length > 1"
                    :label="$t('fields.room')"
                    filled
                    append-icon="mdi-door"
                    :items="rooms"
                    :rules="[rules.required]"
                    v-model="room"
                    return-object
                    item-text="room.name"
                    item-value="room.id"
                    class="notranslate required"
                  />
                  <v-alert dense v-else-if="rooms.length === 1" type="info" color="grey" text>
                    {{ $t("activity_reservation.deliver_to_room") }}
                    {{ rooms[0].room.name }}
                  </v-alert>
                </v-flex>
              </template>
            </v-layout>
          </v-container>
        </v-form>
      </v-flex>
      <v-layout v-else justify-center align-center class="app-scroll-section pa-2">
        <v-progress-circular indeterminate />
      </v-layout>
      <v-layout justify-center class="ma-3 fit-content" row wrap>
        <v-btn
          @click="goToSummary()"
          :disabled="disabledContinueBtn"
          :class="{ 'primary-gradient': !disabledContinueBtn }"
          color="primary"
          centered
          outlined
          block
          >{{ $t("actions.continue") }}</v-btn>
      </v-layout>
    </v-layout>
    <v-layout v-else justify-center align-center>
      <v-progress-circular indeterminate />
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'

import ReservationMixins from '@/mixins/Reservation'
import ActivityMixins from '@/mixins/Activity'

import { attendeeTypeConfigurationTypes } from '@/config'
import routeNames from '@/router/routes'
import router from '@/router'

import { activityReservationStatus } from '@/config'
import { ActivityReservationsRequests } from '@/services/activityReservations.requests'
const activityReservationsService = new ActivityReservationsRequests()

const { NOMINAL, NUMERAL } = attendeeTypeConfigurationTypes

export default {
  beforeRouteEnter (to, from, next) {
    if (localStorage.getItem('reservationMethodType') && localStorage.getItem('reservationMethodType')) {
      next()
    } else {
      router.go(-1)
    }
  },
  data () {
    return {
      moment,
      routeNames,
      loadingWorkingHours: false,
      rules: {
        required: (value) => !!value || '',
        requiredAndNot0: (value) => (!!value && parseInt(value) > 0) || '',
        oneOrMoreNumeralAttendees: () => this.numeralAttendees.some((numeralAttendee) => !!numeralAttendee.quantity) || '',
        oneOrMoreNominalAttendees: () => !!this.nominalAttendees.length || '',
      },
      attendeeTypeConfigurationTypes,
      anotherReservation: null,
    }
  },
  created () {
    const promises = []
    promises.push(this.$store.dispatch('me/getBookings'))
    promises.push(this.$store.dispatch('activities/getActivity', {
      id: this.$route.params.activityId,
    }))
    Promise.all(promises).then(() => {
      this.$store.commit('activityReservations/setField', {
        field: 'activityId',
        value: this.$route.params.activityId,
      })
      this.$store.dispatch('accounts/listMyAccounts').then(accounts => {
        const accountId = this.activityReservations.edited.account ? this.activityReservations.edited.account.id : null
        if (accountId) {
          this.$store.commit('activityReservations/setField', { field: 'account', value: accounts.find(account => account.id === accountId) })
        }
      })
    })
  },
  methods: {
    goToSummary () {
      if (this.$refs.form.validate()) {
        const slots = this.activities.storedSlots[this.date] || null
        if (slots && this.reservationMethod) {
          this.getSlotCapacity().then(async () => {
            if (this.getCapacityStatus({ over: true })) return
            if (this.reservationMethod.type === 'IN_VENUE') {
              this.$router.push({ name: routeNames.reservationSummary.name })
            } else if (this.reservationMethod.type === 'ROOM_SERVICE' && this.account) {
              this.$router.push({ name: routeNames.reservationCatalog.name })
            }
          })
        }
      }
    },
    getAnotherReservationForGroup (date, slot) {
      if (this.activityGroup) {
        var since = moment.tz(date, this.facilityTimeZone).add(slot.split(':')[0], 'hours').add(slot.split(':')[1], 'minutes').subtract(this.activityGroup.groupBlockThreshold, 'ms')
        var until = moment.tz(date, this.facilityTimeZone).add(slot.split(':')[0], 'hours').add(slot.split(':')[1], 'minutes').add(this.activityGroup.groupBlockThreshold, 'ms')
        activityReservationsService
          .listReservations({
            limit: 50,
            offset: 0,
            status: [ activityReservationStatus.PENDING, activityReservationStatus.ACCEPTED, activityReservationStatus.PENDING_MODIFICATION, activityReservationStatus.ATTENDED ],
            sinceDate: since.format('YYYY-MM-DD'),
            sinceTime: moment.duration(since.format('HH:mm'), 'HH:mm').asMilliseconds(),
            untilDate: until.format('YYYY-MM-DD'),
            untilTime: moment.duration(until.format('HH:mm'), 'HH:mm').asMilliseconds(),
          })
          .then(async (response) => {
            this.anotherReservation = response.reservations.find((item) => {
              const reservationMethodType = item.reservation.type
              const reservationMethod = item.reservationMethods.find((extendedRM) => extendedRM.activityReservationMethod.type === reservationMethodType)
              return reservationMethod.activityGroup && reservationMethod.activityGroup.id === this.activityGroup.id
            }) || null
          })
      }
    },
    getReservationTime (reservation) {
      if (reservation.acceptedDate) {
        return moment(reservation.acceptedDate).add(reservation.acceptedTime, 'ms').format('HH:mm')
      } else {
        return moment(reservation.requestedDate).add(reservation.requestedTime, 'ms').format('HH:mm')
      }
    },
  },
  watch: {
    rooms () {
      if (!this.room) {
        this.room = this.rooms.length ? this.rooms[0] : null
      }
    },
  },
  computed: {
    ...mapGetters({
      guests: 'me/guests',
      accountList: 'me/accounts',
      rooms: 'me/allRooms',
      availableDates: 'me/availableDates',
      facilityTimeZone: 'me/facilityTimeZone',
      activityGroup: 'activityReservations/activityGroup',
    }),
    ...mapState({
      bookings: (state) => state.me.bookings,
      state: (state) => state.state,
      accounts: (state) => state.accounts,
    }),
    availableGuests () {
      return JSON.parse(JSON.stringify(this.guests)).map((guest) => {
        guest.disabled = !guest.guest.active || !this.guestAccount(guest)
        return guest
      })
    },
    selectableNominalGuests () {
      return JSON.parse(JSON.stringify(this.guests)).map((guest) => {
        guest.notNominal =
          (this.reservationMethod.attendeeTypeConfiguration === NOMINAL && this.attendeeTypes.length &&
            !this.attendeeTypes.some((aT) => {
              if (aT.guestTypeIds.length) {
                return !!guest.guest.typeId && aT.guestTypeIds.some((guestTypeId) => { return guestTypeId === guest.guest.typeId })
              } else {
                return true
              }
            }))
        guest.disabled = guest.notNominal
        return guest
      })
    },
    availableRooms () {
      return this.rooms
    },
    disabledContinueBtn () {
      return !this.guest || !this.date || !this.slot || this.loadingWorkingHours || this.getCapacityStatus({ over: true }) || (
        (this.reservationMethod.attendeeTypeConfiguration === NUMERAL && !this.numeralAttendees.length) ||
        (this.reservationMethod.attendeeTypeConfiguration === NOMINAL && !this.nominalAttendees.length)
      ) || this.anotherReservation !== null  
    },
  },
  components: {
    AppDropdown: () => import('@/components/app-components/AppDropdown.vue'),
    ReservationTypeChip: () => import('@/components/view-components/reservations/ReservationTypeChip.vue'),
    ReservationDateAndTimeSelector: () => import('@/components/view-components/reservations/ReservationDateAndTimeSelector.vue'),
    AttendeesSelector: () => import('@/components/view-components/activities/AttendeesSelector.vue'),
  },
  mixins: [
    ActivityMixins,
    ReservationMixins,
  ],
}
</script>

<style lang="scss" scoped>
.notranslate {
  transform: none !important;
}
.section-width {
  max-width: 800px !important;
}
.activity-header {
  max-width: 100%;
  min-height: 90px;
  max-height: 90px;
  overflow: hidden;
  display: flex;
  align-items: center;
  position: relative;
  & .activity-title {
    position: absolute;
    z-index: 1;
    top: 8px;
  }
}
</style>
